import { useEffect } from 'react'

import { EDITOR_TOOLS } from '@editor/config/constants'
import { Editor } from '@editor/interfaces/canvas'
import { EditorConfig } from '@editor/interfaces/editor'
import { SubtoolVertex } from '@editor/interfaces/tool'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { setAvailableVertexSubtools } from '@editor/store/toolUtils/vertex'

import { usePolylineSelector } from '../../store/selector'
import { CrackDetectionStage, DetectionKind } from '../interfaces'
import { PolylineToolMode, resetMode } from '../store/core'
import {
  removeWorkingPolyline,
  resetCompletedPolylines,
  resetEditedPolylines,
  setInToolLabelVisibility,
} from '../store/polyline'
import { selectCrackDetectionStage, selectDetectionKind, selectPointForTargetColor } from '../store/selectors'
import useCrackDetectionEditor from './CrackDetection/useEditor'
import usePolylineEditor from './Polyline/useEditor'

const useEditor = (props: Editor): EditorConfig => {
  // Store
  const dispatch = useAppDispatch()
  const mode = usePolylineSelector((state) => state.core.mode)
  const drawingStage = usePolylineSelector(selectCrackDetectionStage)
  const detectionKind = usePolylineSelector(selectDetectionKind)
  const pointForTargetColor = usePolylineSelector(selectPointForTargetColor)
  const selectedTool = useAppSelector((state) => state.editor.selectedTool)
  const polylineDisplaySettings = useAppSelector((state) => state.editorSettings.display.polyline)

  // Flags
  const isToolSelected = selectedTool === EDITOR_TOOLS.POLYLINE

  /**
   * Toggle info panels when tool is selected
   */
  useEffect(() => {
    let message =
      '点群上の任意の点をクリックして延長を計測します。最後の点をクリックするか、ダブルクリックすると描画を完了します。\n最後に右下の保存ボタンをクリックしてください。'
    if (mode === PolylineToolMode.CrackDetection) {
      if (detectionKind === DetectionKind.Chalk) {
        // The colour-pick step only exists for チョーキング, and this text is the only thing
        // that tells the user about it — without it 検出開始 just looks broken.
        message = 'チョーキング周辺で検出したい範囲を描画してください。 描画を完了すると、色の指定に進みます。'

        if (drawingStage === CrackDetectionStage.Preview) {
          message = pointForTargetColor
            ? '色を指定しました。「検出開始」ボタンを押すと、検出が始まります。チョーキング上をもう一度クリックすると、色を選び直せます。'
            : 'チョーキングの上をクリックして、検出したい色を指定してください。カーソルの色見本で確認できます。'
        }
      } else {
        message = 'クラック周辺で検出したい範囲を描画してください。 「検出開始」ボタンを押すと、検出が始まります。'
      }
    }

    if (isToolSelected) {
      dispatch(setAttentionText({ message }))
      // Initialize in-tool label visibility from global settings
      dispatch(
        setInToolLabelVisibility({
          primary: polylineDisplaySettings.primary.label.visible ?? true,
          edge: polylineDisplaySettings.edge.label.visible ?? false,
        }),
      )
    }
  }, [isToolSelected, mode, drawingStage, detectionKind, pointForTargetColor, dispatch, polylineDisplaySettings])

  /**
   * Reset states.
   */
  useEffect(() => {
    const handle = () => {
      dispatch(removeWorkingPolyline())
      dispatch(resetCompletedPolylines())
      dispatch(resetEditedPolylines())
      dispatch(resetMode())
    }

    document.addEventListener('editor:tool:changeReset', handle)
    return () => {
      document.removeEventListener('editor:tool:changeReset', handle)
    }
  }, [dispatch])

  /**
   * When user in Polyline mode, add vertex controls. CrackDetection mode does not have vertex controls.
   */
  useEffect(() => {
    if (isToolSelected && mode === PolylineToolMode.Draw) {
      dispatch(
        setAvailableVertexSubtools([
          SubtoolVertex.AddVertex,
          SubtoolVertex.RemoveVertex,
          SubtoolVertex.MoveVertex,
          SubtoolVertex.ConnectVertex,
        ]),
      )
    } else {
      dispatch(setAvailableVertexSubtools([]))
    }
  }, [isToolSelected, mode, dispatch])

  // Bring in the mode-specific editor hooks
  const crackDetectionEditorHook = useCrackDetectionEditor(props)
  const polylineEditorHook = usePolylineEditor(props)

  return {
    buttons: mode === PolylineToolMode.Draw ? polylineEditorHook.buttons : crackDetectionEditorHook.buttons,
  }
}

export default useEditor
